import { toast } from "components/FluentToast";
import { Formik } from "formik";
import { useCreateFleetVehicleDefaultSetting } from "hooks/data/mutations/useCreateFleetVehicleDefaultSetting";
import { useUpdateFleetVehicleDefaultSetting } from "hooks/data/mutations/useUpdateFleetVehicleDefaultSetting";
import { delay } from "library/delay";
import moment from "moment";
import useActionConfig from "pages/fleet-form/hooks/useActionConfg";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { loadFleetWithInsurersActions, setDialog } from "store/fleetForm";
import { useSelector } from "store/hooks";
import { parseMtplInsuranceLimits } from "utils/mtplInsuranceLimits";
import useDefaultSettingsData from "../hooks/useDefaultSettingsData";
import MtplInsuranceLimitsFormFields, {
  EMPTY_LIMIT_ROW,
  LimitRow,
  limitFieldName,
} from "./MtplInsuranceLimitsFormFields";

type Props = {
  settingId: string;
  attributeName: string;
  isLoading: boolean;
};

// CZ: the MTPL insurance limits of an insurer (the client picks one of them
// for the requested MTPL). Only the valid from date, the insurer and the list
// of limits are entered - the premium type is MTPL and there are no criteria.
const MtplInsuranceLimitsForm = ({
  settingId,
  attributeName,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fleet = useSelector((s) => s.fleetForm.fleetWithPremiums);
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);
  const mtplPremiumTypeId =
    taxonomy.PremiumType.byCode["PremiumType.MTPL"]?.id;

  const { mutate: onUpdateFleetVehicleDefaultSetting } =
    useUpdateFleetVehicleDefaultSetting();
  const { mutate: onCreateFleetVehicleDefaultSetting } =
    useCreateFleetVehicleDefaultSetting();

  const insurerOptions = useActionConfig(fleetAppSettings).insurerOptions;

  const defaultData = useDefaultSettingsData();
  const rowVersion = defaultData?.rowVersion;

  const close = () => {
    dispatch(setDialog({ type: "", isOpen: false }));
  };

  const initialValues = useMemo(() => {
    const limits: LimitRow[] = parseMtplInsuranceLimits(
      defaultData?.attributeValue
    ).map((l) => ({
      personalInjury: String(l.personalInjury),
      propertyDamage: String(l.propertyDamage),
    }));
    return {
      validFromDate: defaultData?.validFromDate
        ? new Date(defaultData.validFromDate)
        : new Date(),
      insurerPartnerNumber: defaultData
        ? insurerOptions?.find(
            (item) => item.value === defaultData.insurerPartnerNumber
          ) ?? null
        : null,
      limits: limits.length > 0 ? limits : [{ ...EMPTY_LIMIT_ROW }],
    };
  }, [defaultData, insurerOptions]);

  const validate = (values) => {
    const errors = {} as Record<string, string>;
    if (!values.validFromDate) {
      errors.validFromDate = t("greco.required");
    }
    if (!values.insurerPartnerNumber?.value) {
      errors.insurerPartnerNumber = t("greco.required");
    }
    const limits: LimitRow[] = values.limits || [];
    if (limits.length === 0) {
      errors.limits = t("bfm.mtplInsuranceLimits.atLeastOneLimit.error");
    }
    const seen = new Set<string>();
    limits.forEach((row, index) => {
      (["personalInjury", "propertyDamage"] as const).forEach((field) => {
        if (!(Number(row[field]) > 0)) {
          errors[limitFieldName(index, field)] = t("greco.required");
        }
      });
      const formatted = `${Number(row.personalInjury)}/${Number(
        row.propertyDamage
      )}`;
      if (seen.has(formatted)) {
        errors[limitFieldName(index, "propertyDamage")] = t(
          "bfm.mtplInsuranceLimits.duplicateLimit.error"
        );
      }
      seen.add(formatted);
    });
    return errors;
  };

  const onSubmit = (values) => {
    // as the other default settings: the selected day at 00:00 UTC
    const localMoment = moment(values.validFromDate);
    const utcValidFromDate = new Date(
      Date.UTC(localMoment.year(), localMoment.month(), localMoment.date())
    );

    const body = {
      validFromDate: utcValidFromDate,
      insurerGroupPolicyNumber: null,
      attributeName,
      attributeValue: JSON.stringify(
        (values.limits as LimitRow[]).map((row) => ({
          personalInjury: Number(row.personalInjury),
          propertyDamage: Number(row.propertyDamage),
        }))
      ),
      premiumType: mtplPremiumTypeId,
      insurerPartnerNumber: values.insurerPartnerNumber?.value,
      // the limits apply to all vehicles of the insurer
      settingCriteria: {},
    };

    const onSuccess = () => {
      toast.success(t("bfm.success"));
      close();
      dispatch(loadFleetWithInsurersActions.trigger({ fleetId: fleet.fleetId }));
    };

    if (!!settingId) {
      onUpdateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          fleetDefaultSettingId: settingId,
          body: { fleetId: fleet.fleetId, ...body, rowVersion },
        },
        { onSuccess }
      );
    } else {
      onCreateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          body: { fleetId: fleet.fleetId, ...body },
        },
        { onSuccess }
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmit(values);
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => (
        <div>
          <form onSubmit={props.handleSubmit}>
            <MtplInsuranceLimitsFormFields
              fieldProps={props}
              insurerOptions={insurerOptions}
              isLoading={isLoading}
            />
          </form>
        </div>
      )}
    </Formik>
  );
};

export default MtplInsuranceLimitsForm;
